import { SuspenseLoader } from '@folio/components/layout/ApplicationLayout/SuspenseLoader';
import { lazy } from 'react';
import {
  ApplicationLayout,
  AuthRequired,
  CreateRetailBrokerageFirmModule,
  DistributionRequestFunnelModule,
  FunnelEndModule,
  IntermediaryDetailsLayout,
  IntermediaryFunnelModule,
  IntermediarySignatureFunnelModule,
  IntermediaryFunnelEndModule,
  NobodyView,
  NotFoundView,
  RedirectModule,
  RetailIntermediaryUsersListModule,
  RetailInviteModule,
  WaitingForReviewModule,
  getAuthRoutes,
  getRoutesFromStepper,
  getStopFunnelRoutes,
  ProductSelectionPage,
} from '@korint/folio';
import { IdentityProviders } from 'korint-sdk/react';
import { Briefcase, HomeIcon, Users } from 'lucide-react';
import {
  Link,
  Outlet,
  type RouteObject,
  ScrollRestoration,
  createBrowserRouter,
  useParams,
} from 'react-router-dom';
import { FloatingChatModule } from '@korint/folio';
import { GarantiePlusAuthLogo } from './features/components/logo/GarantiePlusAuthLogo';
import { GarantiePlusLogo } from './features/components/logo/GarantiePlusLogo';
import { BrokerDashboardLayout } from './features/policy-broker-dashboard/BrokerDashboardLayout';
import { BrokerDashboardPolicyLayout } from './features/policy-broker-dashboard/BrokerDashboardPolicyLayout';
import { BrokerageFirmDocuments } from './pages/BrokerageFirmDocuments';
import { BrokerageFirmInformations } from './pages/BrokerageFirmInformations';
import { BrokerageFirmList } from './pages/BrokerageFirmList';
import { BrokerageFirmReports } from './pages/BrokerageFirmReports';
import { Commissions } from './pages/Commissions';
import { BrokerageFirmUploadDocuments } from './pages/BrokerageFirmUploadDocuments';
import { BrokerPolicyDashboardDocumentsView } from './pages/BrokerPolicyDashboardDocumentsView';
import { BrokerPolicyDashboardInsuredView } from './pages/BrokerPolicyDashboardInsuredView';
import { BrokerPolicyDashboardInvoicesView } from './pages/BrokerPolicyDashboardInvoicesView';
import { BrokerPolicyDashboardOverview } from './pages/BrokerPolicyDashboardOverview';
import { BrokerPolicyDashboardEventsView } from './pages/BrokerPolicyDashboardEventsView';
import { DriverDocumentsView } from './pages/DriverDocumentsView';
import { DriverHomeView } from './pages/DriverHomeView';
import { DriverInformation } from './pages/DriverInformation';
import { IntermediaryDetails } from './pages/IntermediaryDetails';
import { IntermediaryUserCreationForm } from './pages/IntermediaryUserCreationForm';
import { IntermediaryUsersList } from './pages/IntermediaryUsersList';
import { ArchivedNewBusiness } from './pages/ArchivedNewBusiness';
import { OngoingNewBusiness } from './pages/OngoingNewBusiness';
import { PolicyFunnelAsset } from './pages/PolicyFunnelAsset/PolicyFunnelAsset';

import { IntermediaryDetailsHeaderRight } from './features/components/IntermediaryDetailsHeaderRight';
import { IntermediaryDetailsDocuments } from './pages/IntermediaryDetailsDocuments';
import { MyDocuments } from './pages/MyDocuments';
import { PolicyFunnelPolicyHolder } from './pages/PolicyFunnelPolicyHolder';
import { PolicyFunnelQuote } from './pages/PolicyFunnelQuote';
import { Portfolio } from './pages/Portfolio';
import { ProductsDashboard } from './pages/ProductsDashboard';
import { SuperAdminView } from './pages/SuperAdminView';
import {
  invitationControllerInvite,
  policyControllerGeneratePolicySignatureRequest,
} from 'korint-sdk';
import MetabaseAnalytics from './pages/MetabaseAnalytics';

const ConversationModule = lazy(() =>
  import('@korint/folio').then(m => ({ default: m.ConversationModule })),
);

const authRoutes = getAuthRoutes({
  logo: <GarantiePlusAuthLogo />,
  providers: [IdentityProviders.GOOGLE, IdentityProviders.APPLE],
  showLastLoginHint: true,
});

const brokerageFirmStepperRoutes = getRoutesFromStepper({
  steps: [
    {
      path: 'creation',
      element: <BrokerageFirmInformations />,
    },

    {
      path: 'documents-upload',
      element: <BrokerageFirmUploadDocuments />,
      /**
       * @debt we have to keep a custom page because the next button sends documents for signature.
       */
    },
    { path: 'documents', element: <BrokerageFirmDocuments /> },
    { path: 'signature', element: <IntermediarySignatureFunnelModule displayContext="broker" /> },
    {
      path: 'end',
      element: <IntermediaryFunnelEndModule />,
    },
  ],
  basePath: '/brokerage-firm/:brokerageFirmId',
});

const standardRegistrationStepperRoutes = getRoutesFromStepper({
  steps: [
    {
      path: 'creation',
      element: <CreateRetailBrokerageFirmModule />,
      hidden: true,
    },
    {
      path: 'company',
      element: (
        <IntermediaryFunnelModule
          path="company"
          fields={[
            {
              name: 'siret',
              required: true,
              type: 'company',
              apiDataAccessor: 'companyInfo',
              formDataAccessor: 'siret.siret',
              isCustomField: true,
            },
            'garageName',
          ]}
        />
      ),
    },
    {
      path: 'information',
      element: <BrokerageFirmInformations />,
    },
    {
      path: 'product-selection',
      element: (
        <DistributionRequestFunnelModule
          fields={[
            {
              name: 'productId',
              type: 'select',
              required: true,
              options: ['garantiePlusAssistance'],
            },
          ]}
        />
      ),
    },
    {
      path: 'documents-upload',
      element: <BrokerageFirmUploadDocuments />,
    },
    { path: 'documents', element: <BrokerageFirmDocuments /> },
    { path: 'signature', element: <IntermediarySignatureFunnelModule displayContext="broker" /> },
    {
      path: 'end',
      element: <IntermediaryFunnelEndModule />,
    },
  ],
  basePath: '/:parentBrokerageFirmId/brokerage-firm/:brokerageFirmId?',
});

const policyStepperRoutes = getRoutesFromStepper({
  steps: [
    { path: 'asset', element: <PolicyFunnelAsset /> },
    { path: 'quote', element: <PolicyFunnelQuote /> },
    { path: 'policyHolder', element: <PolicyFunnelPolicyHolder /> },
    {
      path: 'end',
      element: (
        <FunnelEndModule
          id="new-business-end"
          additionalComponents={{
            isQuoteTierModuleVisible: false,
            showDocumentsGeneratedAtQuote: true,
          }}
          callback={{
            callToActionCallback: async ({ policyId, branchId, customer }) => {
              await policyControllerGeneratePolicySignatureRequest(
                policyId,
                { deliveryMode: 'EMAIL' },
                { branchId },
              );
              await invitationControllerInvite({
                email: customer.customFields.email as string,
                customerId: customer.id,
                policyId,
                branchId,
                role: 'DRIVER',
              });
            },
          }}
        />
      ),
    },
  ],
  basePath: '/discovery/:customerId/:policyId/:branchId',
});

const DriverRoutes = () => {
  const { policyId } = useParams();

  return (
    <ApplicationLayout
      items={[
        {
          label: 'Accueil',
          link: `/driver/${policyId}`,
          icon: <HomeIcon className="size-4" />,
        },
        {
          label: 'Informations',
          link: `/driver/${policyId}/information`,
          icon: <Users className="size-4" />,
        },
        {
          label: 'Documents',
          link: `/driver/${policyId}/documents`,
          icon: <Briefcase className="size-4" />,
        },
      ]}
      logo={
        <div>
          <GarantiePlusLogo className="h-8 w-auto" />
        </div>
      }
    />
  );
};

const garageInvitationStepperRoutes = getRoutesFromStepper({
  steps: [
    {
      path: 'garage',
      element: (
        <IntermediaryFunnelModule
          path="garage"
          fields={[
            {
              name: 'siret',
              required: true,
              type: 'company',
              apiDataAccessor: 'companyInfo',
              formDataAccessor: 'siret.siret',
              isCustomField: true,
            },
            'garageName',
            {
              name: 'garageSalesRepresentativeName',
              required: false,
              type: 'text',
              isCustomField: true,
            },
          ]}
        />
      ),
    },
    {
      path: 'product-selection',
      element: (
        <DistributionRequestFunnelModule
          fields={[
            {
              name: 'productId',
              type: 'select',
              required: true,
              options: ['garantiePlusAssistance'],
            },
          ]}
        />
      ),
    },
    {
      path: 'retail-invite',
      element: <RetailInviteModule showPreviousButton={false} />,
    },
  ],
  basePath: '/brokerage-firm/:parentBrokerageFirmId/invitation/:brokerageFirmId?',
  afterEndPath: '/brokerage-firm-list',
});

const brokerDashboardRoutes = [
  ...garageInvitationStepperRoutes,
  {
    path: '/ongoing-new-business',
    element: <OngoingNewBusiness />,
  },
  {
    path: '/archived-new-business',
    element: <ArchivedNewBusiness />,
  },
  {
    path: '/portfolio',
    element: <Portfolio />,
  },
  {
    path: '/brokerage-firm-list',
    element: <BrokerageFirmList />,
  },
  {
    path: '/intermediary-users-list',
    element: <IntermediaryUsersList />,
  },
  {
    path: '/intermediary-user-form',
    element: <IntermediaryUserCreationForm />,
  },
  {
    path: '/reports',
    element: <BrokerageFirmReports />,
  },
  {
    path: '/commissions',
    element: <Commissions />,
  },
  {
    path: '/products',
    element: <ProductsDashboard />,
  },
  {
    path: '/analytics',
    element: <MetabaseAnalytics />,
  },
  {
    path: '/conversations',
    element: (
      <SuspenseLoader>
        <ConversationModule />
      </SuspenseLoader>
    ),
  },
  {
    element: <IntermediaryDetailsLayout headerRight={<IntermediaryDetailsHeaderRight />} />,
    children: [
      {
        path: '/brokerage-firm-list/:brokerageFirmId',
        element: <IntermediaryDetails />,
      },
      {
        path: '/brokerage-firm-list/:brokerageFirmId/view-documents',
        element: <IntermediaryDetailsDocuments />,
      },
      {
        path: '/brokerage-firm-list/:brokerageFirmId/users',
        element: <RetailIntermediaryUsersListModule />,
      },
    ],
  },
  {
    path: '/new-business',
    element: <ProductSelectionPage />,
  },
  {
    path: '/my-documents/:brokerageFirmId',
    element: <MyDocuments />,
  },
];

const brokerDashboardPolicyRoutes = [
  {
    path: '/policies/:policyId/:branchId?',
    element: <BrokerPolicyDashboardOverview />,
  },
  {
    path: '/policies/:policyId/:branchId?/customer',
    element: <BrokerPolicyDashboardInsuredView />,
  },
  {
    path: '/policies/:policyId/:branchId?/documents',
    element: <BrokerPolicyDashboardDocumentsView />,
  },
  {
    path: '/policies/:policyId/:branchId?/invoices',
    element: <BrokerPolicyDashboardInvoicesView />,
  },
  {
    path: '/policies/:policyId/:branchId?/events',
    element: <BrokerPolicyDashboardEventsView />,
  },
];

const STOP_FUNNEL_BASE_PATH = '/policies';

const stopFunnelRoutes = getStopFunnelRoutes({
  basePath: `${STOP_FUNNEL_BASE_PATH}/:policyId/stop`,
  afterEndPath: `${STOP_FUNNEL_BASE_PATH}/:policyId`,
});

const routes = [
  ...authRoutes,
  {
    path: '*',
    element: <NotFoundView />,
  },
  {
    // driver routes
    element: (
      <AuthRequired>
        <DriverRoutes />
      </AuthRequired>
    ),
    children: [
      {
        path: '/driver/:policyId',
        element: <DriverHomeView />,
      },
      {
        path: '/driver/:policyId/information',
        element: <DriverInformation />,
      },
      {
        path: '/driver/:policyId/documents',
        element: <DriverDocumentsView />,
      },
    ],
  },
  {
    // broker dashboard routes
    element: (
      <AuthRequired>
        <SuspenseLoader>
          <BrokerDashboardLayout />
        </SuspenseLoader>
      </AuthRequired>
    ),
    children: [
      ...policyStepperRoutes,
      ...brokerDashboardRoutes,
      {
        element: <BrokerDashboardPolicyLayout stopFunnelBasePath={STOP_FUNNEL_BASE_PATH} />,
        children: brokerDashboardPolicyRoutes,
      },
      ...stopFunnelRoutes,
    ],
  },
  {
    // no role routes
    element: (
      <AuthRequired>
        <div className="container mx-auto p-4">
          <Outlet />
        </div>
      </AuthRequired>
    ),
    children: [
      ...brokerageFirmStepperRoutes,
      ...standardRegistrationStepperRoutes,
      {
        path: '/admin',
        element: <SuperAdminView />,
      },
      {
        path: '/',
        element: <RedirectModule logo={<GarantiePlusLogo />} />,
      },
      {
        path: '/redirect',
        element: <RedirectModule logo={<GarantiePlusLogo />} />,
      },
      {
        path: '/waiting',
        element: (
          <WaitingForReviewModule
            title="Votre demande est en cours de revue"
            message="Merci pour votre inscription. La demande de distribution de votre garage sera examinée dans les plus brefs délais."
          />
        ),
      },
      {
        path: '/nobody',
        element: (
          <NobodyView
            message={
              <p>
                Il semble que vous ayez accédé à cette page par erreur ou sans disposer des droits
                nécessaires.
                <br />
                <br />
                <ul className="list-disc pl-4 text-left">
                  <li>
                    Vous souhaitez distribuer le produit Garantie Plus Panne Mécanique : accédez au
                    formulaire dédié{' '}
                    <Link
                      style={{ textDecoration: 'underline' }}
                      to="/brokerage_firm_prh7eVW3UkiEiCr6UW4T4/brokerage-firm/creation"
                    >
                      ici
                    </Link>
                    .
                  </li>
                  <li>
                    Vous avez déjà reçu une invitation : veuillez utiliser le lien reçu par email
                  </li>
                  <li>
                    Vous avez un doute : contactez-nous à{' '}
                    <a
                      style={{ textDecoration: 'underline' }}
                      href="mailto:contact@garantieplus.fr"
                    >
                      contact@garantieplus.fr
                    </a>
                    .
                  </li>
                </ul>
              </p>
            }
          />
        ),
      },
    ],
  },
] satisfies RouteObject[];

export const router = createBrowserRouter([
  {
    element: (
      <>
        <ScrollRestoration />
        <Outlet />
        <FloatingChatModule />
      </>
    ),
    children: routes,
  },
]);
